import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { PhoneFlip } from 'react-bootstrap-icons';
import { useTranslation } from '../i18n';

/**
 * Shows a blocking modal on mobile devices when in landscape orientation,
 * prompting the user to rotate to portrait for the best experience.
 *
 */
const KEYBOARD_VISIBILITY_THRESHOLD = 120; // px difference between layout & visual viewport
const LANDSCAPE_MAX_SHORT_SIDE = 450; // replicate original max-height:450 intent for phones only

// Derive whether we should block the UI asking user to rotate.
// Criteria:
//  - Device considered mobile (shortest screen side < 992)
//  - Landscape orientation (stable, not transient due to keyboard)
//  - Soft keyboard NOT open (avoid false positives when height shrinks)
function useMobileLandscapeBlock(): boolean {
  const [show, setShow] = useState(false);
  const initialInnerHeight = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return; // SSR safety
    if (initialInnerHeight.current == null) {
      initialInnerHeight.current = window.innerHeight;
    }

    const compute = () => {
      if (typeof window === 'undefined') return;

      const screenW = window.screen?.width || window.innerWidth;
      const screenH = window.screen?.height || window.innerHeight;
  const shortestSide = Math.min(screenW, screenH);

      // Stable landscape detection: prefer Screen Orientation API, fallback to dimension compare
      let isLandscape = false;
      const orientationType = (window.screen as any)?.orientation?.type as string | undefined;
      if (orientationType) {
        isLandscape = orientationType.startsWith('landscape');
      } else {
        isLandscape = screenW > screenH; // fallback
      }

      // Soft keyboard detection (visualViewport more accurate on iOS & Chrome)
      const vv = (window as any).visualViewport as VisualViewport | undefined;
      let keyboardOpen = false;
      if (vv) {
        const heightDiff = window.innerHeight - vv.height; // area consumed by overlays (keyboard)
        keyboardOpen = heightDiff > KEYBOARD_VISIBILITY_THRESHOLD;
      } else if (initialInnerHeight.current) {
        keyboardOpen = initialInnerHeight.current - window.innerHeight > KEYBOARD_VISIBILITY_THRESHOLD;
      }

  // Only trigger if truly a small device in landscape: shortest (now landscape height) <= 450
  const smallLandscape = isLandscape && shortestSide <= LANDSCAPE_MAX_SHORT_SIDE;
  setShow(smallLandscape && !keyboardOpen);
    };

    // Compute initially
    compute();

    // Debounce rapid resize/orientation churn (esp. iOS URL bar collapse)
    let raf: number | null = null;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };

    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    (window as any).visualViewport?.addEventListener('resize', schedule);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      (window as any).visualViewport?.removeEventListener('resize', schedule);
    };
  }, []);

  return show;
}

const OrientationModal: React.FC = () => {
  const { t } = useTranslation();
  const isMobileLandscape = useMobileLandscapeBlock();

  return (
    <Modal
      show={isMobileLandscape}
      onHide={() => {}}
      backdrop="static"
      keyboard={false}
      centered
      className="orientation-modal"
    >
      <Modal.Body className="text-center p-4">
        <div className="mb-3" aria-hidden>
          <PhoneFlip className="orientation-icon" size={28} />
        </div>
        <h5 className="fw-semibold mb-2">{t('modal.rotate_device')}</h5>
        <p className="mb-0 text-muted">
          {t('modal.portrait_mode')}
        </p>
      </Modal.Body>
    </Modal>
  );
};

export default OrientationModal;
